import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Stack,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  Checkbox,
  Typography,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { useFormikContext } from "formik";
import {
  getDepartments,
  getJobTitles,
} from "../../../../../api/services/common.service";
import {
  getProfitCenters,
  getPayrollDetails,
  getFrequencies,
} from "../../../../../api/services/employee.service";
import PayrollsPopup from "../../../../../Components/PayrollPopup";
import { SectionBox } from "../../../../../HelperFunctions/HelperFunctions";
import { useAlerts } from "../../../../../Hooks";
import {
  HeaderCell,
  ActionButtons,
} from "../../../../../HelperFunctions/EmployeeFormHelpers";
import { useSnackbar } from "notistack";
import { Dropdown } from "../../../../../HelperFunctions/PayrollDropdown";
import AddJobTitle from "../../../../../QuickAdd/AddJobTitle";
import employeeManagementStyles from "../../../../../styles/employeeManagement.styles";

// --- Constants ---
const BASE_COLUMNS_NO_PC = [
  { name: "Is Default", dataKey: "isDefault", width: "10%" },
  { name: "Department", dataKey: "department", width: "15%" },
  { name: "Job Title", dataKey: "jobTitle", width: "15%" },
  { name: "Item Name", dataKey: "itemName", width: "15%" },
  { name: "Chart of Accounts", dataKey: "chartOfAccounts", width: "21%" },
  { name: "Rate", dataKey: "rate", width: "14%" },
  { name: "Actions", dataKey: "actions", width: "10%" },
];

const BASE_COLUMNS_WITH_PC = [
  { name: "Is Default", dataKey: "isDefault", width: "8%" },
  { name: "Department", dataKey: "department", width: "13%" },
  { name: "Job Title", dataKey: "jobTitle", width: "13%" },
  { name: "Profit Center", dataKey: "profitCenter", width: "13%" },
  { name: "Item Name", dataKey: "itemName", width: "13%" },
  { name: "Chart of Accounts", dataKey: "chartOfAccounts", width: "18%" },
  { name: "Rate", dataKey: "rate", width: "12%" },
  { name: "Actions", dataKey: "actions", width: "10%" },
];

function LocalTextField({ value, onValueChange, onValueBlur, clearOnFocusZero, ...props }) {
    const [localValue, setLocalValue] = React.useState(value ?? "");
    const [isFocused, setIsFocused] = React.useState(false);

    React.useEffect(() => { 
        if (!isFocused) {
            setLocalValue(value ?? ""); 
        }
    }, [value, isFocused]);

    return (
        <TextField
            {...props}
            title={localValue}
            value={localValue}
            onChange={(e) => {
                const val = e.target.value;
                if (onValueChange && onValueChange(val) === false) return;
                setLocalValue(val);
            }}
            onBlur={(e) => { 
                setIsFocused(false);
                if (onValueBlur) onValueBlur(localValue); 
                if (props.onBlur) props.onBlur(e);
            }}
            onFocus={(e) => {
                setIsFocused(true);
                if (clearOnFocusZero && Number(localValue) === 0) {
                    setLocalValue("");
                }
                if (props.onFocus) props.onFocus(e);
            }}
        />
    );
}
// --- Helpers ---
const formatInitialRows = (payrollRows) => {
  if (!payrollRows || payrollRows.length === 0) return [];

  return payrollRows.map((row) => {
    const isUsedVal = row.isUsedEmp || row.IsUsedEMP || row.isUsedEMP || row.IsUsedEmp;
    return {
      ...row,
      isUsedEmp: isUsedVal === true || String(isUsedVal).toLowerCase() === "true",
      isDefault: row.isDefault === true || row.isDefault === 1 ? 1 : 0,
      departmentError: "",
      jobTitleError: "",
      payrollItems: row.payrollItems?.map((item) => ({
        ...item,
        rate: item.rate !== undefined && item.rate !== null ? parseFloat(item.rate).toFixed(2) : "0.00",
      })) || [],
    };
  });
};

const mapPayrollDataWithExistingItems = (payrollData = [], existingPayrollItems = []) => {
  return payrollData.map((item) => {
    const targetItemId = item.PayrollItemID || item.PayrollItemId;
    const existing = existingPayrollItems?.find((ex) => {
      const exId = ex.payrollItemId || ex.PayrollItemId || ex.PayrollItemID;
      if (exId && targetItemId) {
        return String(exId).trim().toLowerCase() === String(targetItemId).trim().toLowerCase();
      }
      const exName = ex.itemName || ex.ItemName;
      const targetName = item.ItemName || item.itemName;
      if (exName && targetName) {
        return String(exName).trim().toLowerCase() === String(targetName).trim().toLowerCase();
      }
      return false;
    }) || {};

    const existingRate =
      existing.rate !== undefined && existing.rate !== null && existing.rate !== ""
        ? Number(existing.rate)
        : null;
    const apiRate =
      item.Rate !== undefined && item.Rate !== null && item.Rate !== ""
        ? Number(item.Rate)
        : null;

    let finalRate = "0.00";
    if (existingRate !== null && !isNaN(existingRate)) {
      finalRate = existingRate.toFixed(2);
    } else if (apiRate !== null && !isNaN(apiRate)) {
      finalRate = apiRate.toFixed(2);
    }

    return {
      payrollDetailId: existing.payrollDetailId ?? existing.Id ?? existing.ID ?? null,
      payrollDeptJobId:
        item.PayrollItemDeptJobDetailsID ||
        item.PayrollDeptJobId ||
        existing.payrollDeptJobId ||
        0,

      payrollItemId:
        targetItemId ||
        existing.payrollItemId ||
        "",

      itemName: item.ItemName || existing.itemName || "",
      account: item.COA || existing.account || "",

      rate: finalRate,
    };
  });
};

const PayrollDetails = ({ onLoadingChange, corporationId }) => {
  const { values, setFieldValue, errors, touched, handleBlur, resetForm, dirty } = useFormikContext();
  const { enqueueSnackbar } = useSnackbar();
  // Prioritize the explicitly passed corporationId, especially for Quick Add scenarios.
  const corpId = corporationId || values.corporation;

  // --- State ---
  const [departments, setDepartments] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [profitCenterAutoValue, setProfitCenterAutoValue] = useState("");
  const [openAddJobTitle, setOpenAddJobTitle] = useState(false);
  const [pendingJobTitleRowId, setPendingJobTitleRowId] = useState(null);
  const [rows, setRows] = useState(() => formatInitialRows(values.payrollRows));
  
  const prevPayrollRowsRef = useRef(values.payrollRows);
  const { popup: alertPopup, showDeleteConfirm, hidePopup } = useAlerts();

  // --- Columns Configuration ---
  const columns = useMemo(() => {
    return profitCenters.length > 0 ? BASE_COLUMNS_WITH_PC : BASE_COLUMNS_NO_PC;
  }, [profitCenters.length]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loadingDetails || configLoading);
    }
  }, [loadingDetails, configLoading, onLoadingChange]);

  // --- Missing Payroll Details Fetcher ---
  useEffect(() => {
    const fetchMissingPayrollDetails = async () => {
      setLoadingDetails(true);
      let updated = false;

      const fetchPromises = rows.map(async (row) => {
        const hasItems = row.payrollItems && row.payrollItems.some((item) => item.itemName);
        if (row.department && row.jobTitle && !hasItems) {
          try {
            const payload = {
              corpId: corpId,
              IncDeptId: row.department,
              JobId: row.jobTitle,
            };
            if (profitCenters.length > 0 && row.profitCenter) {
              payload.PCID = row.profitCenter;
            }
            const response = await getPayrollDetails(payload);
            const payrollData = response?.EmpPayrollItemDetails || [];

            if (payrollData && payrollData.length > 0) {
              updated = true;
              const newItems = mapPayrollDataWithExistingItems(payrollData, row.payrollItems);
              return { ...row, payrollItems: newItems };
            }
          } catch (error) {
            console.error("Failed to fetch payroll details", error);
          }
        }
        return row;
      });

      const newRows = await Promise.all(fetchPromises);

      if (updated) {
        setRows(newRows);
        if (!dirty) {
          resetForm({
            values: {
              ...values,
              payrollRows: newRows,
            },
          });
        }
      }
      setLoadingDetails(false);
    };

    fetchMissingPayrollDetails();
  }, [rows]);

  // --- Corporation Specific Data ---
  useEffect(() => {
    if (!corpId) {
      setDepartments([]);
      setProfitCenters([]);
      setProfitCenterAutoValue("");
      return;
    }

    const fetchCorpData = async () => {
      try {
        const [depts, centers] = await Promise.all([
          getDepartments(),
          getProfitCenters(corpId)
        ]);
        setDepartments(depts || []);
        setProfitCenters(centers || []);
        if (centers?.length === 1) {
          setProfitCenterAutoValue(centers[0].value);
        } else {
          setProfitCenterAutoValue("");
        }
      } catch (error) {
        console.error("Error fetching corporation data:", error);
      }
    };

    fetchCorpData();
  }, [corpId]);

  // --- Global Dropdown Data ---
  useEffect(() => {
    const fetchGlobalData = async () => {
      try {
        const [jobs, freqs] = await Promise.all([
          getJobTitles(),
          getFrequencies()
        ]);
        setJobTitles(jobs || []);
        setFrequencies(freqs || []);
      } catch (error) {
        console.error("Error fetching global configurations:", error);
      }
    };
    fetchGlobalData();
  }, []);

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (JSON.stringify(values.payrollRows) !== JSON.stringify(rows)) {
      setFieldValue("payrollRows", rows);
    }
  }, [rows, values.payrollRows, setFieldValue]);

  useEffect(() => {
    const prev = prevPayrollRowsRef.current;
    const current = values.payrollRows;
    if (prev && prev.length > 0 && (!current || current.length === 0)) {
      setRows([]);
    }
    prevPayrollRowsRef.current = current;
  }, [values.payrollRows]);

  // --- UI Handlers ---
  const handleDefaultChange = useCallback((rowId, checked) => {
    setRows((prev) =>
      prev.map(
        (row) =>
          row.id === rowId
            ? { ...row, isDefault: checked ? 1 : 0 }
            : checked
              ? { ...row, isDefault: 0 }
              : row,
      ),
    );
  }, []);

  const handleAddRow = useCallback(() => {
    if (!values.FrequencyID) {
      enqueueSnackbar("Please select Pay frequency", { variant: "error" });
      return;
    }

    setRows((prev) => {
      const newId = prev.length > 0 ? Math.max(...prev.map((r) => r.id)) + 1 : 1;
      const newRow = {
        id: newId,
        isDefault: prev.length === 0 ? 1 : 0,
        department: "",
        jobTitle: "",
        departmentError: "",
        jobTitleError: "",
        profitCenter: profitCenterAutoValue,
        payrollItems: [
          { payrollDeptJobId: null, itemName: "", account: "", rate: "0.00" },
        ],
      };
      return [...prev, newRow];
    });
  }, [values.FrequencyID, profitCenterAutoValue, enqueueSnackbar]);

  const handleDeleteRow = useCallback((idToDelete) => {
    const rowToDelete = rows.find((r) => r.id === idToDelete);
    if (!rowToDelete) return;

    const isDefault = rowToDelete.isDefault === 1;
    const hasData = Boolean(
      rowToDelete.department ||
      rowToDelete.jobTitle ||
      (rowToDelete.payrollItems &&
        rowToDelete.payrollItems.some(
          (it) => it.itemName || it.account || it.rate,
        )),
    );
    
    const confirmDelete = () => {
      setRows((prev) => {
        let remainingRows = prev.filter((r) => r.id !== idToDelete);
        
        if (isDefault && remainingRows.length > 0) {
          const nextDefaultId = remainingRows[0].id;
          remainingRows = remainingRows.map((r) => r.id === nextDefaultId ? { ...r, isDefault: 1 } : r);
        }
        return remainingRows;
      });

      const idsToDelete = rowToDelete.payrollItems
        .map((item) => item.payrollDetailId)
        .filter(Boolean);
      
      if (idsToDelete.length > 0) {
        setFieldValue("deletedPayrollIds", [
          ...(values.deletedPayrollIds || []),
          ...idsToDelete,
        ]);
      }
    };

    if (rowToDelete.isUsedEmp) {
      showDeleteConfirm(
        "These details are being used in Payroll Entry, Would you like to delete?",
        confirmDelete,
      );
    } else if (hasData) {
      showDeleteConfirm(
        "Are you sure you want to delete this row?",
        confirmDelete,
      );
    } else {
      // If the row is empty, delete it without confirmation
      confirmDelete();
    }
  }, [rows, values.deletedPayrollIds, setFieldValue, showDeleteConfirm]);


  const handleCellChange = useCallback(async (rowId, field, value, additionalData = null) => {
    let updatedRow;
    const newRows = rows.map((r) => {
      if (r.id === rowId) {
        const newRowData = { ...r, [field]: value };
        if (field === "department") {
          const dept = departments.find((d) => d.value === value);
          newRowData.departmentName = dept ? dept.label : "";
          newRowData.departmentError = value ? "" : "Please select Department";
        } else if (field === "jobTitle") {
          const job =
            jobTitles.find((j) => j.value === value) || additionalData;
          newRowData.jobTitleName = job ? job.label : "";
          newRowData.jobTitleError = value ? "" : "Please select Job Title";
        }
        updatedRow = newRowData;
        return updatedRow;
      }
      return r;
    });

    if (updatedRow && (field === "department" || field === "jobTitle")) {
      const isDuplicate = newRows.some(
        (r) =>
          r.id !== rowId &&
          r.department === updatedRow.department &&
          r.jobTitle === updatedRow.jobTitle &&
          updatedRow.department &&
          updatedRow.jobTitle,
      );
      if (isDuplicate) {
        updatedRow = {
          ...updatedRow,
          departmentError: "Duplicate combination is detected",
          jobTitleError: "Duplicate combination is detected",
        };
        enqueueSnackbar("Duplicate combination is detected", {
          variant: "error",
        });
      }
    }

    setRows(newRows);

    if (updatedRow && (field === "department" || field === "jobTitle")) {
      if (updatedRow.department && updatedRow.jobTitle) {
        setConfigLoading(true);
        try {
          const payload = {
            corpId: corpId,
            IncDeptId: updatedRow.department,
            JobId: updatedRow.jobTitle,
            Id: updatedRow.payrollItems?.[0]?.payrollDeptJobId || 0
          };
          if (profitCenters.length > 0 && updatedRow.profitCenter) {
            payload.PCID = updatedRow.profitCenter;
          }
          const response = await getPayrollDetails(payload);
          const payrollData = response?.EmpPayrollItemDetails || [];

          if (payrollData && payrollData.length > 0) {
            const newItems = mapPayrollDataWithExistingItems(payrollData, updatedRow.payrollItems);
            setRows((prev) =>
              prev.map((r) =>
                r.id === rowId ? { ...r, payrollItems: newItems } : r,
              ),
            );
          } else {
            setRows((prev) =>
              prev.map((r) =>
                r.id === rowId
                  ? {
                      ...r,
                      payrollItems: [
                        {
                          payrollDeptJobId: null,
                          itemName: "",
                          account: "",
                          rate: "0.00",
                        },
                      ],
                    }
                  : r,
              ),
            );
            enqueueSnackbar(
              "This Department and Job Title does not contain Item Name and COA",
              { variant: "error" },
            );
          }
        } catch (error) {
          console.error("Failed to fetch payroll details", error);
        } finally {
          setConfigLoading(false);
        }
      }
    }
  }, [rows, departments, jobTitles, corpId, enqueueSnackbar, profitCenters.length]);

  const handleItemChange = useCallback((rowId, itemIndex, field, value) => {
    setRows((prevRows) => prevRows.map((row) => {
      if (row.id === rowId) {
        const currentItem = row.payrollItems?.[itemIndex];

        if (field === "rate") {
          if (!currentItem?.itemName || !currentItem?.account) return row;
          if (value !== "" && !/^\d*\.?\d*$/.test(value)) return row;
        }

        const newItems = [...row.payrollItems];
        newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
        return { ...row, payrollItems: newItems };
      }
      return row;
    }));
  }, []);

  return (
    <Stack sx={employeeManagementStyles.payrollDetailsStack}>
      {/* Top Controls Row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={employeeManagementStyles.payrollDetailsTopRow}
      >
        <Stack direction="row" alignItems="center" spacing={5}>
          <Box sx={employeeManagementStyles.payFrequencyLabelBox}>
            <Typography
              sx={employeeManagementStyles.payFrequencyLabelText}
            >
              Pay Frequency <span style={{ color: "red" }}>*</span>
            </Typography>
          </Box>
          <Dropdown
            name="FrequencyID"
            value={
              frequencies.find((f) => f.value === values.FrequencyID) || {
                label: "--Select--",
                value: "",
              }
            }
            options={[{ label: "--Select--", value: "" }, ...frequencies]}
            getOptionLabel={(option) => option.label}
            isEqual={(option, value) => option.value === value.value}
            onChange={(name, newValue) => {
              const frequencyId = newValue?.value || "";
              const selectedFreq = frequencies.find(
                (f) => f.value === frequencyId,
              );
              if (selectedFreq) {
                setFieldValue("FrequencyID", frequencyId);
                setFieldValue("PayFrequency", selectedFreq.label);
              } else {
                setFieldValue("FrequencyID", "");
                setFieldValue("PayFrequency", "");
              }
            }}
            onBlur={handleBlur}
            error={touched.FrequencyID && Boolean(errors.FrequencyID)}
            sx={employeeManagementStyles.payrollFrequencyDropdown}
          />
        </Stack>

        <Button
          variant="contained"
          size="small"
          onClick={handleAddRow}
          sx={employeeManagementStyles.addRowBtn}
        >
          Add Row
        </Button>
      </Stack>

      <SectionBox height={"100%"} title="Payroll Details">
        {loadingDetails ? null : (
          <TableContainer
            sx={employeeManagementStyles.payrollTableContainer}
          >
            <Table
              size="small"
              stickyHeader
              sx={employeeManagementStyles.payrollTable}
            >
              <colgroup>
                {columns.map((col, idx) => (
                  <col key={idx} style={{ width: col.width }} />
                ))}
              </colgroup>

              <TableHead>
                <TableRow>
                  {columns.map((col, idx) => (
                    <HeaderCell key={idx} title={col.name} width={col.width} />
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id} hover>
                    {/* Is Default */}
                    <TableCell
                      sx={employeeManagementStyles.tableCellVerticalTop}
                    >
                      <Stack alignItems="center" spacing={0.5} sx={employeeManagementStyles.checkboxStack}>
                        <Checkbox
                          checked={row.isDefault === 1}
                          onChange={(e) =>
                            handleDefaultChange(row.id, e.target.checked)
                          }
                          size="small"
                          color="primary"
                        />

                        {/* {row.id === rows[0]?.id && row.isDefault === 1 && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "10px",
                              color: "#777",
                              textAlign: "center",
                              lineHeight: 1.1,
                            }}
                          >
                            Mark as default
                            <br />
                            for this role
                          </Typography>
                        )} */}
                      </Stack>
                    </TableCell>

                    {/* Department */}
                    <TableCell sx={employeeManagementStyles.tableCellBorderRight}>
                      <Dropdown
                        name={`payrollRows[${index}].department`}
                        value={
                          departments.find(
                            (d) => d.value === row.department,
                          ) || { label: "--Select--", value: "" }
                        }
                        options={[
                          { label: "--Select--", value: "" },
                          ...departments,
                        ]}
                        getOptionLabel={(option) => option.label}
                        isEqual={(option, value) => option.value === value.value}
                        onChange={(name, newValue) =>
                          handleCellChange(
                            row.id,
                            "department",
                            newValue?.value || "",
                          )
                        }
                        error={!!row.departmentError}
                        sx={employeeManagementStyles.tableInputHeight30}
                        popperSx={employeeManagementStyles.dropdownPopperMaxContent}
                      />
                    </TableCell>

                    {/* Job Title */}
                    <TableCell sx={employeeManagementStyles.tableCellBorderRight}>
                      <Dropdown
                        name={`payrollRows[${index}].jobTitle`}
                        value={
                          jobTitles.find((j) => j.value === row.jobTitle) || {
                            label: "--Select--",
                            value: "",
                          }
                        }
                        options={[
                          { label: "--Select--", value: "" },
                          { label: "Add New", value: "ADD_NEW" },
                          ...jobTitles,
                        ]}
                        getOptionLabel={(option) => option.label}
                        isEqual={(option, value) => option.value === value.value}
                        renderOption={(props, option, { selected }) => (
                          <li
                            {...props}
                            style={{
                              color: selected ? 'white' : (option.value === "ADD_NEW" ? "#d32f2f" : "inherit"),
                              fontWeight: option.value === "ADD_NEW" ? 500 : "inherit",
                            }}
                          >
                            {option.label}
                          </li>
                        )}
                        onChange={(name, newValue) => {
                          if (newValue?.value === "ADD_NEW") {
                            setPendingJobTitleRowId(row.id);
                            setOpenAddJobTitle(true);
                          } else {
                            handleCellChange(row.id, "jobTitle", newValue?.value || "");
                          }
                        }}
                        error={!!row.jobTitleError}
                        sx={employeeManagementStyles.tableInputHeight30}
                        popperSx={employeeManagementStyles.dropdownPopperMaxContent}
                      />
                    </TableCell>

                    {/* Profit Center */}
                    {profitCenters.length > 0 && (
                      <TableCell sx={employeeManagementStyles.tableCellBorderRight}>
                        <Dropdown
                          name={`payrollRows[${index}].profitCenter`}
                          value={
                            profitCenters.find(
                              (pc) => pc.value === row.profitCenter,
                            ) || { label: "--Select--", value: "" }
                          }
                          options={[
                            { label: "--Select--", value: "" },
                            ...profitCenters,
                          ]}
                          getOptionLabel={(option) => option.label}
                          isEqual={(option, value) => option.value === value.value}
                          onChange={(name, newValue) =>
                            handleCellChange(
                              row.id,
                              "profitCenter",
                              newValue?.value || "",
                            )
                          }
                          sx={employeeManagementStyles.tableInputHeight30}
                          popperSx={employeeManagementStyles.dropdownPopperMaxContent}
                        />
                      </TableCell>
                    )}

                    {/* Item Name */}
                    <TableCell sx={employeeManagementStyles.tableCellBorderRight}>
                      <Stack spacing={2.5}>
                        {row.payrollItems.map((item, idx) => (
                          <Typography
                            key={idx}
                            sx={employeeManagementStyles.itemText}
                          >
                            {item.itemName}
                          </Typography>
                        ))}
                      </Stack>
                    </TableCell>

                    {/* Chart of Accounts */}
                    <TableCell sx={employeeManagementStyles.tableCellBorderRight}>
                      <Stack spacing={2.5}>
                        {row.payrollItems.map((item, idx) => (
                          <Typography
                            key={idx}
                            sx={employeeManagementStyles.itemText}
                          >
                            {item.account}
                          </Typography>
                        ))}
                      </Stack>
                    </TableCell>

                    {/* Rate */}
                    <TableCell sx={employeeManagementStyles.tableCellBorderRight15}>
                      <Stack spacing={1}>
                        {row.payrollItems.map((item, idx) => (
                          <LocalTextField
                            key={idx}
                            size="small"
                            value={item.rate}
                            disabled={!item.itemName || !item.account}
                            onValueChange={(val) => /^\d*\.?\d*$/.test(val)}
                            onValueBlur={(value) => {
                              let val = parseFloat(value);
                              if (isNaN(val)) val = 0;
                              const roundedRate = (
                                Math.round((val + Number.EPSILON) * 100) / 100
                              ).toFixed(2);
                              handleItemChange(row.id, idx, "rate", roundedRate);
                            }}
                            clearOnFocusZero={true}
                            inputProps={{
                              style: {
                                textAlign: "right",
                                fontSize: 12,
                                fontWeight: 600,
                                padding: "6px 8px",
                              },
                            }}
                            sx={employeeManagementStyles.fullWidthInput}
                          />
                        ))}
                      </Stack>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={employeeManagementStyles.tableCellAction}>
                      <ActionButtons id={row.id} onDelete={handleDeleteRow} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionBox>
      <PayrollsPopup
        open={alertPopup.open}
        popupType={alertPopup.type}
        message={alertPopup.message}
        onConfirm={alertPopup.onConfirm}
        onCancel={hidePopup}
      />
      <AddJobTitle
        open={openAddJobTitle}
        onClose={() => {
          setOpenAddJobTitle(false);
          setPendingJobTitleRowId(null);
        }}
        onSuccess={async (createdJobTitle) => {
          const jobs = await getJobTitles();
          setJobTitles(jobs);
          if (pendingJobTitleRowId !== null && createdJobTitle?.value) {
            handleCellChange(
              pendingJobTitleRowId,
              "jobTitle",
              createdJobTitle.value,
              createdJobTitle,
            );
          }
          setOpenAddJobTitle(false);
          setPendingJobTitleRowId(null);
        }}
      />
    </Stack>
  );
};

export default PayrollDetails;
